// OWNED BY the documents implementation agent (see ../DocumentsTab.tsx ownership note).
//
// One COMPLETED signature document, rendered read-only in the חוזים חתומים
// group (mock s19). The row is auto-filed from the signatures data (shared
// `signatures:{contactId}` cache key) — nothing is copied into the documents
// subcollection. Download streams through the AUTHENTICATED signed-pdf
// endpoint (the app-wide convention; raw GCS URLs are never handed to the UI).

import { useState } from "react";
import { Box, Stack, Typography, IconButton, CircularProgress } from "@material-ui/core";
import { alpha } from "@material-ui/core/styles";
import { AssignmentOutlined, GetApp, TaskAlt } from "@material-ui/icons";
import { useSnackbar } from "notistack5";
import { getDocumentFileUrl } from "services/signPublic";
import { questionnaireDownloadLabel, questionnaireName } from "utils/signatureQuestionnaireName";
import {
  formatDocDate,
  downloadSignedContractPdf,
  downloadQuestionnaireDocumentPdf
} from "./documentsData";
import type { SignatureDocument } from "../../../../../../@types/signatures";

type Props = {
  doc: SignatureDocument;
};

export function SignedContractRow({ doc }: Props): JSX.Element {
  const { enqueueSnackbar } = useSnackbar();
  const [downloading, setDownloading] = useState(false);

  const signedDate = formatDocDate(doc.completedAt || doc.sentAt);
  const metaParts = [
    signedDate ? `נחתם דיגיטלית ${signedDate}` : "נחתם דיגיטלית",
    "תויק אוטומטית מטאב החתימות",
    "PDF"
  ];

  const handleDownload = async (): Promise<void> => {
    setDownloading(true);
    try {
      await downloadSignedContractPdf(doc);
    } catch (error) {
      console.error("Documents tab: signed-contract download failed", error);
      enqueueSnackbar("הורדת החוזה החתום נכשלה", { variant: "error", autoHideDuration: 4000 });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={1.5}
      sx={{
        px: 1,
        py: 1.25,
        borderRadius: 1,
        "&:hover": { bgcolor: "action.hover" }
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          flexShrink: 0,
          borderRadius: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "success.dark",
          bgcolor: (theme) => alpha(theme.palette.success.main, 0.16)
        }}
      >
        <TaskAlt fontSize="small" />
      </Box>

      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography variant="subtitle2" noWrap dir="auto">
          {doc.templateNameSnapshot || doc.formattedNumber || "מסמך חתום"}
        </Typography>
        <Typography variant="caption" noWrap component="div" sx={{ color: "text.secondary" }}>
          {metaParts.join(" · ")}
        </Typography>
      </Box>

      <IconButton
        size="small"
        aria-label="הורדת החוזה החתום"
        disabled={downloading}
        onClick={() => void handleDownload()}
      >
        {downloading ? <CircularProgress size={18} /> : <GetApp fontSize="small" />}
      </IconButton>
    </Stack>
  );
}

// One seller-questionnaire (שאלון מוכר) file, auto-filed alongside its signed
// contract. Download goes through the authenticated per-document file endpoint
// (same convention as SignatureDocRow's questionnaire menu item).
export function SellerQuestionnaireRow({ doc }: Props): JSX.Element {
  const metaParts = [
    doc.templateNameSnapshot ? `מתוך ${doc.templateNameSnapshot}` : "",
    formatDocDate(doc.completedAt || doc.sentAt),
    "PDF"
  ].filter(Boolean);

  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={1.5}
      sx={{
        px: 1,
        py: 1.25,
        borderRadius: 1,
        "&:hover": { bgcolor: "action.hover" }
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          flexShrink: 0,
          borderRadius: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "info.dark",
          bgcolor: (theme) => alpha(theme.palette.info.main, 0.16)
        }}
      >
        <AssignmentOutlined fontSize="small" />
      </Box>

      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography variant="subtitle2" noWrap dir="auto">
          {questionnaireName(doc.sellerQuestionnaire?.listedFor)}
          {doc.formattedNumber ? ` · ${doc.formattedNumber}` : ""}
        </Typography>
        <Typography variant="caption" noWrap component="div" sx={{ color: "text.secondary" }}>
          {metaParts.join(" · ")}
        </Typography>
      </Box>

      <IconButton
        size="small"
        aria-label={questionnaireDownloadLabel(doc.sellerQuestionnaire?.listedFor)}
        component="a"
        href={getDocumentFileUrl(doc.id, { type: "questionnaire", download: true })}
        target="_blank"
        rel="noopener noreferrer"
      >
        <GetApp fontSize="small" />
      </IconButton>
    </Stack>
  );
}

// A questionnaire-only document (שאלון בלבד): the owner filled and signed the questionnaire on its
// own, so it IS the document — no contract it was "from". Filed beside the questionnaires above,
// never with the signed contracts. Its file is the document's signed PDF, downloaded through the
// AUTHENTICATED signed-pdf endpoint like a signed contract.
export function QuestionnaireDocumentRow({ doc }: Props): JSX.Element {
  const { enqueueSnackbar } = useSnackbar();
  const [downloading, setDownloading] = useState(false);
  const filledDate = formatDocDate(doc.completedAt || doc.sentAt);
  const metaParts = [filledDate ? `מולא ונחתם דיגיטלית ${filledDate}` : "מולא ונחתם דיגיטלית", "PDF"];

  const handleDownload = async (): Promise<void> => {
    setDownloading(true);
    try {
      await downloadQuestionnaireDocumentPdf(doc);
    } catch (error) {
      console.error("Documents tab: questionnaire download failed", error);
      enqueueSnackbar("הורדת השאלון נכשלה", { variant: "error", autoHideDuration: 4000 });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={1.5}
      sx={{
        px: 1,
        py: 1.25,
        borderRadius: 1,
        "&:hover": { bgcolor: "action.hover" }
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          flexShrink: 0,
          borderRadius: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "info.dark",
          bgcolor: (theme) => alpha(theme.palette.info.main, 0.16)
        }}
      >
        <AssignmentOutlined fontSize="small" />
      </Box>

      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography variant="subtitle2" noWrap dir="auto">
          {questionnaireName(doc.sellerQuestionnaire?.listedFor)}
          {doc.formattedNumber ? ` · ${doc.formattedNumber}` : ""}
        </Typography>
        <Typography variant="caption" noWrap component="div" sx={{ color: "text.secondary" }}>
          {metaParts.join(" · ")}
        </Typography>
      </Box>

      <IconButton
        size="small"
        aria-label={questionnaireDownloadLabel(doc.sellerQuestionnaire?.listedFor)}
        disabled={downloading}
        onClick={() => void handleDownload()}
      >
        {downloading ? <CircularProgress size={18} /> : <GetApp fontSize="small" />}
      </IconButton>
    </Stack>
  );
}
